import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';

import { trackEvent } from '../index';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

export const EVENT_IDS = {
  START_ACTIVATE_PREMIUM_TOP: '2935',
  START_ACTIVATE_PREMIUM_BOTTOM: '2936',
  START_ACTIVATE_FREEMIUM_TOP: '2937',
  START_ACTIVATE_FREEMIUM_BOTTOM: '2938',
  START_ACTIVATE_PREMIUM_FOOTER: '2939',
  ACTIVATE_LISTING: '2961',
  ACTIVATE_MULTIPLEX: '9759',
  COMPLETES_PREMIUM_UPSELL: '3413',
  POST_LISTING_DASHBOARD_MODAL_TOUR_AVAILABILITY_IMPRESSION: '4308',
  POST_LISTING_DASHBOARD_MODAL_TOUR_AVAILABILITY_BUTTON: '6636',
} as const;

// Pay & Publish control labels for start_activate_listing trigger_source_nm and activate_multiplex trigger_object_nm.
export const ACTIVATE_MULTIPLEX_TRIGGER_OBJECT = {
  GO_PREMIUM: 'go_premium',
  PUBLISH_WITHOUT_PREMIUM: 'publish_without_premium',
  PUBLISH_WITH_PREMIUM: 'publish_with_premium',
} as const;

export type ActivateMultiplexTriggerObject =
  (typeof ACTIVATE_MULTIPLEX_TRIGGER_OBJECT)[keyof typeof ACTIVATE_MULTIPLEX_TRIGGER_OBJECT];

// Fallback when multiplexTriggerObject is omitted; prefer passing an explicit value at call sites.
export const DEFAULT_ACTIVATE_MULTIPLEX_TRIGGER_OBJECT = ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.PUBLISH_WITHOUT_PREMIUM;

// https://dataportal.zillowgroup.net/event-registry/2935
export const emitStartActivatePremiumTopEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.START_ACTIVATE_PREMIUM_TOP,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'top_of_page',
      trigger_source_nm: ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.PUBLISH_WITH_PREMIUM,
    },
    semantic: {
      semantic_event_nm: 'start_activate_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2936
export const emitStartActivatePremiumBottomEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.START_ACTIVATE_PREMIUM_BOTTOM,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'bottom_of_page',
      trigger_source_nm: ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.PUBLISH_WITH_PREMIUM,
    },
    semantic: {
      semantic_event_nm: 'start_activate_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2937
export const emitStartActivateFreemiumTopEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.START_ACTIVATE_FREEMIUM_TOP,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'top_of_page',
      trigger_source_nm: ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.PUBLISH_WITHOUT_PREMIUM,
    },
    semantic: {
      semantic_event_nm: 'start_activate_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2938
export const emitStartActivateFreemiumBottomEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.START_ACTIVATE_FREEMIUM_BOTTOM,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'bottom_of_page',
      trigger_source_nm: ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.PUBLISH_WITHOUT_PREMIUM,
    },
    semantic: {
      semantic_event_nm: 'start_activate_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2939
export const emitStartActivatePremiumFooterEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.START_ACTIVATE_PREMIUM_FOOTER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'footer_component',
      trigger_source_nm: ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.GO_PREMIUM,
    },
    semantic: {
      semantic_event_nm: 'start_activate_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9759
export const emitActivateMultiplexListingEvent = ({
  currentProperty,
  user,
  triggerObjectNm,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerObjectNm: ActivateMultiplexTriggerObject;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '265',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ACTIVATE_MULTIPLEX,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'multiplex_activation',
      trigger_source_nm: 'publish_listing',
      trigger_object_nm: triggerObjectNm,
    },
    semantic: {
      semantic_event_nm: 'activate_multiplex',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2961
export const emitActivateListingEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ACTIVATE_LISTING,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'activate_listing',
    },
    semantic: {
      semantic_event_nm: 'activate_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4308
export const emitTourAvailabilityImpressionEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.POST_LISTING_DASHBOARD_MODAL_TOUR_AVAILABILITY_IMPRESSION,
      event_type_version_id: '3',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_congrats_modal',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'touring_modal',
      trigger_source_nm: 'no_trigger_source',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties', 'zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6636
export const emitSetTourAvailabilityButtonEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.POST_LISTING_DASHBOARD_MODAL_TOUR_AVAILABILITY_BUTTON,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_congrats_modal',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tour_module',
      trigger_source_nm: 'setup_tour_availability',
    },
    semantic: {
      semantic_event_nm: 'tour_scheduling_entrypoint',
      topic_tag_txt: ['zrm_properties', 'zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3413
export const emitCompletesPremiumUpsellEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.COMPLETES_PREMIUM_UPSELL,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'post_activation',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'confirm_upgrade',
    },
    semantic: {
      semantic_event_nm: 'upgrade_to_premium',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });
